<template>
  <v-toolbar dense class="pl-2 pr-2">
    <v-btn
      variant="flat"
      color="green-darken-3"
      size="small"
      class="report-btn"
      :disabled="isLoading"
      @click="createReport"
    >
      <v-icon start> mdi-pencil </v-icon>
      Nieuw rapport
    </v-btn>

    <v-btn
      variant="flat"
      color="secondary"
      size="small"
      class="report-btn ml-2"
      :loading="isCreatingLocation"
      :disabled="isLoading"
      @click="createLocationReport"
    >
      <v-icon start> mdi-eye-plus </v-icon>
      {{ tenantSeenLabel }}
    </v-btn>

    <v-spacer />

    <v-btn
      variant="flat"
      size="small"
      color="yellow-darken-3"
      class="report-btn refresh-btn"
      :disabled="isLoading"
      title="Ververs rapporten"
      @click="refreshReports"
    >
      <v-icon> mdi-refresh </v-icon>
    </v-btn>
  </v-toolbar>
</template>

<script>
import { useFormsStore } from "@/stores/formsStore";
import { useReportsStoreRefactor } from "@/stores/reportsStoreRefactor";
import { useDayReportsStoreRefactor } from "@/stores/dayReportsStoreRefactor";
import { useReportCategoriesStoreRefactor } from "@/stores/reportCategoriesStoreRefactor";
import { useAuthStoreRefactor } from "@/stores/authStoreRefactor";
import { useTenantStore } from "@/stores/tenantStore";
import { ref, reactive, onMounted, computed } from "vue";
import moment from "moment";

export default {
  name: "ReportButtonsRefactor",
  props: ["account_id", "day_report_id"],
  emits: ["report-created", "refresh"],
  setup(props, { emit }) {
    const formsStore = useFormsStore();
    const reportsStore = useReportsStoreRefactor(); // Using refactored store
    const dayReportsStore = useDayReportsStoreRefactor();
    const reportCategoriesStore = useReportCategoriesStoreRefactor();
    const authStore = useAuthStoreRefactor();
    const tenantStore = useTenantStore();
    const isCreatingLocation = ref(false);

    // Computed properties from store
    const isLoading = computed(() => reportsStore.isLoading);

    const tenantName = computed(
      () => tenantStore.tenantName || tenantStore.tenantDisplayName || "",
    );
    const tenantSeenLabel = computed(() => {
      const n = tenantName.value?.trim();
      return n ? `Gezien ${n}` : "Gezien";
    });

    // Initialize report data with defaults
    const report = reactive({
      report_category: "Communicatie",
      date: moment().format("YYYY-MM-DD"),
      time: moment().format("HH:mm"),
      description: "",
      account_id: props.account_id,
      day_report_id: props.day_report_id,
      duration: 1,
      selected_traits: [],
      user_id: null,
    });

    // Initialize component
    const initialize = async () => {
      try {
        console.log("ReportButtonsRefactor: Initializing...");

        // Get current user for user_id
        if (!authStore.user) {
          await authStore.getCurrentUser();
        }
        if (authStore.user?.id) {
          report.user_id = authStore.user.id;
        }

        console.log("ReportButtonsRefactor: Initialized successfully");
      } catch (error) {
        console.error("ReportButtonsRefactor: Error initializing:", error);
      }
    };

    const updateReportData = () => {
      // Set current date and time
      report.time = moment().format("HH:mm");
      report.date = moment().format("YYYY-MM-DD");

      // Make sure we have the latest prop values
      report.account_id = props.account_id;

      // Get day_report_id from props if available
      if (props.day_report_id) {
        report.day_report_id = props.day_report_id;
      }
    };

    const createReport = () => {
      updateReportData();

      console.log("ReportButtonsRefactor: Creating report with basic data:", {
        ...report,
      });

      formsStore.edit({
        type: "report",
        item: { ...report },
      });
    };

    const createLocationReport = async () => {
      try {
        isCreatingLocation.value = true;
        updateReportData();

        // Ensure we have categories loaded
        if (!reportCategoriesStore.reportCategories?.length) {
          try {
            await reportCategoriesStore.getAll(true);
          } catch (e) {}
        }

        // Pick first visual=true category; fallback to 'Locatie'
        const visualCat = (reportCategoriesStore.reportCategories || []).find(
          (c) => c.visual === true,
        );
        const categoryName = visualCat?.name || "Locatie";

        // Set specific values for location/visual report
        const locationReport = {
          ...report,
          description: tenantName.value || "",
          report_category: categoryName,
        };

        console.log(
          "ReportButtonsRefactor: Creating location report:",
          locationReport,
        );

        // Use the refactored store (automatic success alerts handled by apiClient)
        const createdReport = await reportsStore.create(locationReport);

        // After creating a report, refresh the day report if needed
        if (props.day_report_id) {
          await dayReportsStore.getDayReport(props.day_report_id);
          console.log(
            "ReportButtonsRefactor: Day report refreshed after creating report",
          );
        }

        // Reset to default values
        report.description = "";
        report.report_category = "Communicatie";

        // Notify parent about the new report
        emit("report-created", createdReport);

        console.log(
          "ReportButtonsRefactor: Location report created successfully",
        );
      } catch (error) {
        console.error(
          "ReportButtonsRefactor: Error creating location report:",
          error,
        );
        // Error alerts are automatically handled by apiClient
      } finally {
        isCreatingLocation.value = false;
      }
    };

    const refreshReports = () => {
      console.log("ReportButtonsRefactor: Refresh button clicked");
      emit("refresh");
    };

    // Lifecycle
    onMounted(() => {
      initialize();
    });

    return {
      // Reactive data
      report,
      isCreatingLocation,

      // Computed properties
      isLoading,
      tenantSeenLabel,

      // Methods
      createReport,
      createLocationReport,
      refreshReports,
    };
  },
};
</script>

<style scoped>
/* Regular desktop styling */
.report-buttons-container {
  padding: 8px 12px;
  background: transparent;
}

/* 📱 MOBILE-ONLY: Smaller regular buttons */
@media (max-width: 599px) {
  /* Compact container */
  .report-buttons-container {
    padding: 6px 8px !important;
  }

  /* Smaller buttons on mobile */
  .report-btn {
    font-size: 0.75rem !important;
    min-height: 28px !important;
    height: 28px !important;
    padding: 0 8px !important;
  }

  /* Smaller icons */
  .report-btn .v-icon {
    font-size: 14px !important;
  }

  /* Refresh button - keep same size but more compact */
  .refresh-btn {
    min-width: 28px !important;
    width: 28px !important;
    padding: 0 4px !important;
  }

  /* Tighter spacing between buttons */
  .report-btn.ml-2 {
    margin-left: 6px !important;
  }
}
</style>
